import React from 'react';
import PropTypes from 'prop-types';

import MetricLoadingView from '../../../components/MetricLoadingView.jsx';

import PanelWithUpsell from './PanelWithUpsell.jsx';


import {
    useIsExportingPPT,
    useRegisterSlideElementsForPPTExport
} from '../../../hooks/useExport.js';

import {
    useCurationConfigValue
} from '../../../hooks/widgetConfiguration.js';

import {
    useSetExportConfig
} from '../../../hooks/exportConfigurationHooks.js';

import useListeningCurationFilterValues from '../hooks/useListeningCurationFilterValues.js';

import {
    RIQPopover
} from '../../../components/RIQPopover.jsx';

import RIQMetricLineChart from '../../../components/RIQMetricLineChart.jsx';
import SingleGlanceMetricDisplay from './SingleGlanceMetricDisplay.jsx';
import ViewContentButton from './ViewContentButton.jsx';

import {
    useIntervalOptions,
    IntervalSelector
} from './IntervalSelector.jsx';

import useContentModal from '../hooks/useContentModal.jsx';

import {
    listeningDefinitions
} from './enums/listeningDefinitions.jsx';

function AreaChartTooltip({areaConfigs, datum, onTooltipFocusChange, showContentModal, placement}) {

    return (
        <>
            {datum && <div
                className='tlw-pointer-events-auto'
                onMouseEnter={() => onTooltipFocusChange(true)}
                onMouseLeave={() => onTooltipFocusChange(false)}
            >
                <RIQPopover
                    triggerNode={<div/>}
                    className='tlw-flex tlw-w-full tlw-items-center tlw-gap-[10px] tlw-text-md tlw-font-normal'
                    containerClassName='tlw-max-w-none'
                    placement={placement || 'top'}
                    portalNode={document.body}
                    additionalHookProps={{
                        visible: true
                    }}
                >
                    <div>
                        <div className='tlw-m-0 tlw-rounded-t-md tlw-border tlw-border-l-0 tlw-border-r-0 tlw-border-t-0 tlw-border-solid tlw-border-gray-200 tlw-px-7 tlw-py-4 tlw-text-md tlw-text-gray-700'>
                            {datum.tooltipTitle || datum.xValue}
                        </div>
                        <div className='tlw-flex tlw-px-7 tlw-py-4.5 tlw-text-left'>
                            {_.flatMap(areaConfigs,
                                ({metrics}) => {

                                    return _.map(metrics, ({dataKey, expandedFormatter}) => {
                                        const colorKeyClassName = `tlw-mr-2 tlw-inline-flex tlw-h-6 tlw-w-6 tlw-rounded-sm ${dataKey === 'positive' ? 'tlw-bg-green-400' : 'tlw-bg-red-400'}`;
                                        let newSentiment;
                                        switch (dataKey) {
                                            case 'positive':
                                                newSentiment = 'Positive';
                                                break;
                                            case 'negative':
                                                newSentiment = 'Negative';
                                                break;
                                            default:
                                                newSentiment = undefined;
                                                break;
                                        }

                                        const metricFilterOverrides = {
                                            startDate: datum.newStartDate,
                                            endDate: datum.newEndDate,
                                            sentiment: newSentiment
                                        };

                                        return (
                                            <div
                                                key={`tooltip-info-${dataKey}`}
                                                className='tlw-m-0 tlw-w-1/2 tlw-border-0 tlw-border-solid tlw-border-gray-200 tlw-px-0 tlw-py-4 tlw-pr-7 last_tlw-pl-7 even_tlw-border-l'>

                                                <div className='tlw-whitespace-nowrap tlw-text-11 tlw-font-semibold tlw-uppercase tlw-text-gray-700'>
                                                    <div className={colorKeyClassName} />
                                                    {dataKey}
                                                </div>
                                                <div className='tlw-text-xl tlw-font-bold tlw-leading-36 tlw-text-gray-900'>
                                                    {expandedFormatter(_.isFinite(datum[dataKey]) ? Math.abs(datum[dataKey]) : datum[dataKey])}
                                                </div>
                                                <ViewContentButton
                                                    showContentModal={showContentModal}
                                                    metricFilterOverrides={metricFilterOverrides}
                                                />
                                            </div>
                                        );
                                    });
                                })}
                        </div>
                    </div>
                </RIQPopover>
            </div>}
        </>
    );
}


PositiveNegativeChartWidget.propTypes = {
    instanceId: PropTypes.string.isRequired,
    className: PropTypes.string,
    pdfExportConfig: PropTypes.object,
    appSpecificExportConfig: PropTypes.object
};

function filtersWithSentiment(metricFilters, sentiment) {

    const existingSentimentFilters = metricFilters.listening.sentiment || _.values(RivalIQ.metrics.new.enums.ListeningSentiments);
    const newSentimentFilters = _.intersection(existingSentimentFilters, [sentiment]);

    const newFilters = _.defaults({
        listening: {
            ...metricFilters.listening,
            sentiment: newSentimentFilters
        }
    }, metricFilters);

    if (_.isEmpty(newSentimentFilters)) {
        newFilters.listening.matchNone = true;
    }

    return newFilters;
}

export default function PositiveNegativeChartWidget({instanceId, className, pdfExportConfig, appSpecificExportConfig}) {

    const {showContentModal, ContentModal} = useContentModal(instanceId);

    const {
        title = 'Sentiment',
        description,
        footer: footerContextView,
        positivePosts,
        negativePosts
    } = listeningDefinitions[instanceId] || {};

    useSetExportConfig(instanceId, {
        title: title,
        supportedExportFormats: _.without(_.values(RivalIQ.enums.exportFormat), RivalIQ.enums.exportFormat.CSV),
        supportedExportTypes: _.values(RivalIQ.enums.exportType),
        pdfExportConfig,
        appSpecificExportConfig
    });

    const metricFilters = useListeningCurationFilterValues(instanceId);

    useRegisterSlideElementsForPPTExport(instanceId, {
        header: {
            title,
            subtitle: appSpecificExportConfig?.timePeriodStr
        }
    });

    const interval = useCurationConfigValue('graphInterval');
    const metricRequests = {
        positive: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: [interval],
            measure: 'count',
            filters: filtersWithSentiment(metricFilters, 'Positive')
        },
        negative: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: [interval],
            measure: 'count',
            filters: filtersWithSentiment(metricFilters, 'Negative')
        },
        totalPositive: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: [],
            measure: 'count',
            filters: filtersWithSentiment(metricFilters, 'Positive')
        },
        totalNegative: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: [],
            measure: 'count',
            filters: filtersWithSentiment(metricFilters, 'Negative')
        }
    };

    const intervalSelector =
        <IntervalSelector
            instanceId={instanceId}
        />;
    const {selectedInterval} = useIntervalOptions(instanceId);

    const panelDescription = `${description} grouped by ${interval}.`;

    // NOTE adjust top margin for PPT export to avoid empty second slide
    let metricSummaryTopMargin = 'tlw-mt-7';
    if (useIsExportingPPT()) {
        metricSummaryTopMargin = 'tlw-mt-4';
    }

    return (
        <PanelWithUpsell
            instanceId={instanceId}
            title={title}
            description={panelDescription}
            exportSubtitle={appSpecificExportConfig?.timePeriodStr}
            footerContextView={footerContextView}
            showViewMore={true}
            className={className}
            additionalTitleBarControls={intervalSelector}
        >
            <MetricLoadingView
                instanceId={instanceId}
                metricRequests={metricRequests}>
                {
                    (metricResults) => {
                        const dataByInterval = RIQMetricLineChart.collateMetricResults(selectedInterval.id, metricRequests, metricResults, ['positive', 'negative'], {
                            negative: result => {
                                return _.isFinite(_.last(result.negative)) ? -_.last(result.negative) : _.last(result.negative);
                            }
                        });

                        // NOTE: these correspond to tlw-text-green-400 and tlw-text-red-400.
                        const positiveLineColor = '#8bb84f';
                        const negativeLineColor = '#ef592f';
                        const chartConfigs = [{
                            chartName: `${title} by ${selectedInterval.name}`,

                            metrics: [{
                                showAreaChart: true,
                                dataKey: 'positive',
                                lineColor: positiveLineColor,
                                fillColor: positiveLineColor,
                                abbreviatedFormatter: RivalIQ.metrics.new.MetricFormatter.getFormatter(metricRequests.positive, metricResults.positive, RivalIQ.metrics.new.enums.MetricFormatTypes.abbreviated),
                                expandedFormatter: RivalIQ.metrics.new.MetricFormatter.getFormatter(metricRequests.positive, metricResults.positive, RivalIQ.metrics.new.enums.MetricFormatTypes.expanded),
                                metricRequest: metricRequests.positive
                            }, {
                                showAreaChart: true,
                                dataKey: 'negative',
                                lineColor: negativeLineColor,
                                fillColor: negativeLineColor,
                                abbreviatedFormatter: RivalIQ.metrics.new.MetricFormatter.getFormatter(metricRequests.negative, metricResults.negative, RivalIQ.metrics.new.enums.MetricFormatTypes.abbreviated),
                                expandedFormatter: RivalIQ.metrics.new.MetricFormatter.getFormatter(metricRequests.negative, metricResults.negative, RivalIQ.metrics.new.enums.MetricFormatTypes.expanded),
                                metricRequest: metricRequests.negative
                            }]
                        }];

                        const tooltipContent = (
                            <AreaChartTooltip
                                areaConfigs={chartConfigs}
                                showContentModal={showContentModal}
                                placement='top'
                            />
                        );

                        return (
                            <>
                                <div className={`tlw-mb-4 ${metricSummaryTopMargin} tlw-flex tlw-h-36 tlw-px-7`}>
                                    <SingleGlanceMetricDisplay
                                        description={positivePosts}
                                        metricRequest={metricRequests.totalPositive}
                                        metricResult={metricResults.totalPositive}
                                        metricClasses={`tlw-text-green-400`}
                                        metricName='Positive Posts' />
                                    <SingleGlanceMetricDisplay
                                        description={negativePosts}
                                        metricRequest={metricRequests.totalNegative}
                                        metricResult={metricResults.totalNegative}
                                        metricClasses={`tlw-text-red-400`}
                                        metricName='Negative Posts' />
                                </div>
                                <div className='tlw-border-0 tlw-border-t tlw-border-solid tlw-border-gray-200 tlw-p-7.5'>
                                    <RIQMetricLineChart
                                        chartKey={instanceId}
                                        dataByInterval={dataByInterval}
                                        lineChartHeight={275}
                                        title={`Posts by ${selectedInterval.name}`}
                                        description={description}
                                        chartConfigs={chartConfigs}
                                        tooltipContent={tooltipContent}
                                    />
                                </div>

                                {ContentModal}
                            </>
                        );
                    }
                }
            </MetricLoadingView>
        </PanelWithUpsell>
    );
}
